import { useState } from "react";
import { Car, ChevronDown, ChevronRight, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import InlineEditField from "@/components/InlineEditField";
import api from "@/lib/api";

/**
 * VehiculeCard — vue compacte mobile (iter132).
 *
 * Layout :
 *   [Plaque] [KM]                              [Chevron]
 *   ──────────────────────────────────────────────────────
 *   (accordéon, fermé par défaut)
 *     - VIN (encart ambré, éditable inline)
 *     - Marque, Modèle, 1ère MEC, Cylindrée, etc.
 *
 * Choix UX :
 * - Sur mobile, on n'affiche immédiatement que Plaque + KM (les 2 infos
 *   les plus consultées par le mécano). Le reste est replié.
 * - Boutons "copier" toujours visibles (utiles sur tactile).
 * - Plaque + VIN éditables inline ; autres champs en lecture seule
 *   (modifiables via la fiche véhicule complète).
 *
 * Composant partagé OR / Devis / Facture via DocumentHeader.
 */
export default function VehiculeCard({ vehicule, defaultOpen = false, onUpdate, extraStat = null }) {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(null);
  const [localVeh, setLocalVeh] = useState(vehicule);
  const v = localVeh || vehicule;

  if (!v) return null;

  const updateField = async (field, value) => {
    const { data } = await api.patch(`/vehicules/${v.id}`, { [field]: value });
    setLocalVeh(data);
    onUpdate?.(data);
  };

  // iter182 — Wrapper robuste : fallback textarea si l'API Clipboard n'est pas
  // disponible (Safari, HTTP non-sécurisé, permission denied dans certains iframes).
  const writeClipboard = (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
      }
    } catch (_e) { /* fallthrough */ }
    // Fallback DOM
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    } catch (_e) { /* silent */ }
    return Promise.resolve();
  };

  const copy = (label, value) => {
    if (!value) return;
    writeClipboard(String(value));
    setCopied(label);
    toast.success(`${label} copié`);
    setTimeout(() => setCopied(null), 1500);
  };

  // iter182 — Copier toutes les infos véhicule d'un coup pour un catalogue de pièces.
  const copyAll = () => {
    const parts = [];
    if (v.plaque) parts.push(`Plaque : ${v.plaque}`);
    if (vin) parts.push(`VIN : ${vin}`);
    if (v.marque) parts.push(`Marque : ${v.marque}`);
    if (v.modele) parts.push(`Modèle : ${v.modele}`);
    if (v.premiere_mec) parts.push(`1ère MEC : ${v.premiere_mec}`);
    if (v.cylindree) parts.push(`Cylindrée : ${v.cylindree}`);
    if (v.carburant || v.energie) parts.push(`Carburant : ${v.carburant || v.energie}`);
    if (v.puissance) parts.push(`Puissance : ${v.puissance} kW`);
    if (v.km_dernier != null && v.km_dernier !== "") parts.push(`KM : ${v.km_dernier}`);
    const text = parts.join(" · ");
    if (!text) {
      toast.error("Aucune info véhicule à copier");
      return;
    }
    writeClipboard(text);
    setCopied("Tout");
    toast.success("Infos véhicule copiées");
    setTimeout(() => setCopied(null), 1500);
  };

  const vin = v.vin || v.numero_chassis || v.chassis;
  const kmDernier = v.km_dernier;

  // Champs détails (cachés dans l'accordéon)
  const detailRows = [
    ["Marque", v.marque, "marque"],
    ["Modèle", v.modele, "modele"],
    ["1ère mise en circulation", v.premiere_mec, "premiere_mec", "date"],
    ["Cylindrée", v.cylindree],
    ["Carburant", v.carburant || v.energie],
    ["Puissance", v.puissance ? `${v.puissance} kW` : null],
    ["Assurance", v.assurance_compagnie],
    // iter251 — Référence client : éditable ici, mais affichée dans l'en-tête du document
    // (bloc « En-tête / Références ») pour ne pas la montrer deux fois.
    ["Référence client", v.reference_vehicule, "reference_vehicule"],
  ].filter(([, val, editField]) => editField || (val != null && val !== ""));

  // Nombre total d'infos disponibles dans l'accordéon (VIN + autres champs)
  const totalDetails = detailRows.length + (vin ? 1 : 0);

  // iter251 — Si le document fournit son propre compteur (ex. km à la dépose d'un OR),
  // on n'affiche que celui-là : deux « KM » côte à côte prêtaient à confusion.
  const statsBlock = (
    <>
        {!extraStat && (<>
        {/* KM dernier passage */}
        <div className="flex-shrink-0 min-w-0 text-left sm:text-right">
          <div className="flex items-center justify-start sm:justify-end gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              KM
            </span>
            {kmDernier != null && kmDernier !== "" && (
              <button
                type="button"
                onClick={() => copy("KM", kmDernier)}
                className="text-gray-300 hover:text-[#52a675] transition-colors p-0.5"
                title="Copier le kilométrage"
                data-testid="copy-km"
              >
                {copied === "KM" ? (
                  <Check className="w-3 h-3 text-emerald-600" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </button>
            )}
          </div>
          {/* iter262 — km éditable partout (OR / devis / facture) : le dernier
              relevé saisi devient le km de référence du véhicule en base. */}
          <div
            className="font-mono text-sm font-semibold text-gray-900"
            data-testid="vehicule-km-value"
          >
            <InlineEditField
              value={kmDernier}
              onSave={(val) => updateField("km_dernier", val === null ? null : parseInt(val, 10))}
              type="number"
              inputMode="numeric"
              placeholder="Ajouter"
              formatDisplay={(v) =>
                v != null && v !== "" ? `${Number(v).toLocaleString("fr-CH")} km` : null
              }
              testId="vehicule-km-inline"
              inputClassName="w-24 font-mono text-sm"
            />
          </div>
        </div>
        </>)}
      {extraStat}
    </>
  );

  return (
    <div
      className="mt-2 border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden"
      data-testid="vehicule-card"
    >
      {/* Top bar — toujours visible : Plaque + KM + Chevron */}
      <div
        className="flex items-center gap-2 px-3 py-2.5"
        data-testid="vehicule-topbar"
      >
        {/* Plaque (éditable inline) */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              Plaque
            </span>
            {v.plaque && (
              <button
                type="button"
                onClick={() => copy("Plaque", v.plaque)}
                className="text-gray-300 hover:text-[#52a675] transition-colors p-0.5"
                title="Copier la plaque"
                data-testid="copy-plaque"
              >
                {copied === "Plaque" ? (
                  <Check className="w-3 h-3 text-emerald-600" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </button>
            )}
          </div>
          <div className="font-mono text-sm font-semibold text-gray-900 break-words">
            <InlineEditField
              value={v.plaque}
              onSave={(val) => updateField("plaque", val)}
              placeholder="Plaque…"
              testId="vehicule-plaque-inline"
              inputClassName="w-full font-mono text-sm font-semibold"
            />
          </div>
          {/* iter247 — marque/modèle ici plutôt qu'en doublon dans l'en-tête du document */}
          {(v.marque || v.modele) && (
            <div className="text-xs text-gray-600 truncate" data-testid="vehicule-modele-label">
              {[v.marque, v.modele].filter(Boolean).join(" ")}
            </div>
          )}
        </div>

        {/* iter249 — les compteurs passent sur une 2e ligne en mobile (place gagnée) */}
        <div className="hidden sm:flex items-start gap-4 flex-shrink-0">
          {statsBlock}
        </div>

        {/* iter182 — Bouton "Copier tout" (plaque + VIN + marque/modèle/…) */}
        <button
          type="button"
          onClick={copyAll}
          className="flex-shrink-0 p-1.5 rounded hover:bg-emerald-50 text-gray-500 hover:text-emerald-700 transition-colors"
          title="Copier toutes les infos véhicule (utile pour catalogue de pièces)"
          data-testid="copy-vehicule-all"
        >
          {copied === "Tout" ? (
            <Check className="w-4 h-4 text-emerald-600" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>

        {/* Chevron toggle */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex-shrink-0 p-1.5 -mr-1 rounded hover:bg-gray-100 transition-colors"
          title={open ? "Masquer les détails" : "Afficher les détails"}
          aria-expanded={open}
          data-testid="vehicule-card-toggle"
        >
          {open ? (
            <ChevronDown className="w-4 h-4 text-gray-600" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-600" />
          )}
        </button>
      </div>

      {/* iter249 — mobile : plaque seule en haut, compteurs en dessous */}
      <div className="flex sm:hidden items-start justify-start gap-6 px-3 pb-2.5 -mt-1">
        {statsBlock}
      </div>

      {/* Accordéon — détails */}
      {open && (
        <div className="px-3 pb-3 border-t border-gray-100" data-testid="vehicule-details">
          {/* VIN (encart ambré, critique côté atelier) */}
          <div className="pt-3">
            <div
              className={`bg-amber-50/40 border rounded-md p-2.5 ${
                vin ? "border-amber-200" : "border-dashed border-amber-300"
              }`}
              data-testid="vehicule-vin"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                  N° VIN / Châssis
                </span>
                {vin && (
                  <button
                    type="button"
                    onClick={() => copy("VIN", vin)}
                    className="text-amber-600 hover:text-amber-800 transition-colors p-1"
                    title="Copier le VIN"
                    data-testid="copy-vin"
                  >
                    {copied === "VIN" ? (
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                )}
              </div>
              <div className="font-mono text-sm font-semibold text-gray-900 break-all select-all">
                <InlineEditField
                  value={vin}
                  onSave={(val) => updateField("vin", val)}
                  placeholder="Ajouter le VIN…"
                  testId="vehicule-vin-inline"
                  inputClassName="w-full font-mono text-sm"
                />
              </div>
            </div>
          </div>

          {/* Autres infos — grille 2 colonnes mobile-first */}
          {detailRows.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2.5 text-xs mt-3">
              {detailRows.map(([label, value, editField, editType]) => (
                <div key={label} className="min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      {label}
                    </span>
                    <button
                      type="button"
                      onClick={() => copy(label, value)}
                      className="text-gray-300 hover:text-[#52a675] transition-colors p-0.5 -mr-1"
                      title={`Copier ${label}`}
                      data-testid={`copy-${label
                        .toLowerCase()
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "")
                        .replace(/[^a-z0-9]/g, "-")}`}
                    >
                      {copied === label ? (
                        <Check className="w-3 h-3 text-emerald-600" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                    </button>
                  </div>
                  <div className="font-mono text-sm text-gray-900 break-words mt-0.5">
                    {editField ? (
                      <InlineEditField
                        value={value}
                        onSave={(val) => updateField(editField, val)}
                        type={editType || "text"}
                        placeholder={label}
                        testId={`vehicule-${editField}-inline`}
                        inputClassName="w-full font-mono text-sm"
                      />
                    ) : (
                      value
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalDetails === 0 && (
            <p className="text-xs text-gray-400 italic mt-2">
              Aucun détail supplémentaire renseigné.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
